<template>
  <div v-if="hasAvailability" class="sp-finder-result-hours-badge" :class="classModifiers" part="base">
    <button
      v-if="hasExpandableDetails"
      class="sp-finder-result-hours-badge__trigger"
      type="button"
      :aria-expanded="isExpanded.toString()"
      :aria-controls="panelId"
      :aria-label="triggerAriaLabel"
      part="trigger"
      @click="toggle"
    >
      <sp-icon class="sp-finder-result-hours-badge__icon" name="time-filled"></sp-icon>
      <span class="sp-finder-result-hours-badge__status">{{ statusLabel }}</span>
      <span class="sp-finder-result-hours-badge__summary">
        {{ summaryLabel }}
        <sp-caret class="sp-finder-result-hours-badge__caret" :active="isExpanded" size="small"></sp-caret>
      </span>
    </button>

    <div
      v-else
      class="sp-finder-result-hours-badge__trigger sp-finder-result-hours-badge__trigger--static"
      part="trigger"
    >
      <sp-icon class="sp-finder-result-hours-badge__icon" name="time-filled"></sp-icon>
      <span class="sp-finder-result-hours-badge__status">{{ statusLabel }}</span>
      <span class="sp-finder-result-hours-badge__summary">{{ summaryLabel }}</span>
    </div>

    <transition name="expand">
      <div v-if="showHoursDetails" :id="panelId" class="sp-finder-result-hours-badge__panel" part="panel">
        <dl v-if="hoursRows.length" class="sp-finder-result-hours-badge__list">
          <div v-for="row in hoursRows" :key="`${row.day}-${row.times}`" class="sp-finder-result-hours-badge__row">
            <dt class="sp-finder-result-hours-badge__day">{{ row.day }}</dt>
            <dd class="sp-finder-result-hours-badge__times">{{ row.times }}</dd>
          </div>
        </dl>
        <p v-else-if="alwaysOpenText" class="sp-finder-result-hours-badge__note">{{ alwaysOpenText }}</p>
      </div>
    </transition>

    <div v-if="hasSupportingDetails" class="sp-finder-result-hours-badge__supporting-details" part="supporting-details">
      <!-- Notes are intentionally escaped ({{ }}), not v-html — specialNote is admin free-text. -->
      <!-- TODO: for link support, pass sanitized/structured content, not raw v-html. -->
      <p v-if="note" class="sp-finder-result-hours-badge__note">{{ note }}</p>
      <p v-if="specialNote" class="sp-finder-result-hours-badge__note">{{ specialNote }}</p>

      <div v-if="specialHours.length" class="sp-finder-result-hours-badge__special-hours">
        <h3 class="sp-finder-result-hours-badge__special-hours-title">{{ specialHoursHeading }}</h3>
        <dl class="sp-finder-result-hours-badge__special-hours-list">
          <div
            v-for="specialHour in specialHours"
            :key="`${specialHour.name}-${specialHour.text}`"
            class="sp-finder-result-hours-badge__row"
          >
            <dt class="sp-finder-result-hours-badge__day">{{ specialHour.name }}</dt>
            <dd class="sp-finder-result-hours-badge__times">{{ specialHour.text }}</dd>
          </div>
        </dl>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { randomUUID } from "#/shared/utils/string";
import { useAddressAvailability } from "#ce/composables/useAddressAvailability";

const props = defineProps({
  availability: {
    type: Object,
    default: undefined,
  },
});

const isExpanded = ref(false);
const panelId = `finder-result-hours-${randomUUID()}`;

const {
  hasAvailability,
  hasExpandableDetails,
  isCurrentlyOpen,
  isClosingSoon,
  statusLabel,
  summaryLabel,
  hoursRows,
  specialHours,
  specialHoursHeading,
  specialNote,
  note,
  alwaysOpenText,
  toggleLabel,
} = useAddressAvailability(() => props.availability);

const triggerAriaLabel = computed(() => toggleLabel(isExpanded.value));
const showHoursDetails = computed(() => isExpanded.value && hasExpandableDetails.value);
const hasSupportingDetails = computed(() => Boolean(note.value || specialNote.value || specialHours.value.length));

const classModifiers = computed(() => ({
  "--status-open": isCurrentlyOpen.value && !isClosingSoon.value,
  "--status-closing": isClosingSoon.value,
}));

function toggle() {
  isExpanded.value = !isExpanded.value;
}
</script>

<style>
:host {
  display: block;
}
</style>

<style lang="scss" scoped>
.sp-finder-result-hours-badge {
  /* Layout */
  --trigger-gap: var(--sp-comp-finder-result-hours-badge-trigger-gap, var(--sp-ref-spacing-4));
  --trigger-summary-caret-gap: var(
    --sp-comp-finder-result-hours-badge-trigger-summary-caret-gap,
    var(--sp-ref-spacing-2)
  );
  --trigger-min-height: var(--sp-comp-finder-result-hours-badge-trigger-min-height, var(--icon-size));
  --panel-width: var(--sp-comp-finder-result-hours-badge-panel-width, 22rem);
  --panel-gap: var(--sp-comp-finder-result-hours-badge-panel-gap, var(--sp-ref-spacing-2));
  --panel-row-gap: var(--sp-comp-finder-result-hours-badge-panel-row-gap, var(--sp-ref-spacing-2));
  --panel-margin-block-start: var(
    --sp-comp-finder-result-hours-badge-panel-margin-block-start,
    var(--sp-ref-spacing-4)
  );

  /* Color */
  --trigger-color: var(--sp-comp-finder-result-hours-badge-trigger-color, var(--sp-sys-color-on-surface));
  --status-open-color: var(--sp-comp-finder-result-hours-badge-status-open-color, var(--sp-sys-color-success, #00804d));
  --status-closing-color: var(
    --sp-comp-finder-result-hours-badge-status-closing-color,
    var(--sp-sys-color-warning, #ad5c00)
  );
  --status-closed-color: var(
    --sp-comp-finder-result-hours-badge-status-closed-color,
    var(--sp-sys-color-danger, #dd1d26)
  );
  --summary-color: var(--sp-comp-finder-result-hours-badge-summary-color, var(--sp-sys-color-on-surface));

  /* Typography */
  --trigger-font: var(--sp-comp-finder-result-hours-badge-trigger-font, var(--sp-sys-typescale-body-short));
  --status-font: var(--sp-comp-finder-result-hours-badge-status-font, var(--sp-sys-typescale-body-bold-short));
  --panel-font: var(--sp-comp-finder-result-hours-badge-panel-font, var(--sp-sys-typescale-body-small-short));
  --panel-day-font: var(--sp-comp-finder-result-hours-badge-panel-day-font, var(--sp-sys-typescale-body-bold-short));

  /* Icon */
  --icon-size: var(--sp-comp-finder-result-hours-badge-icon-size, var(--sp-sys-icon-size-small));

  --status-color: var(--status-closed-color);
  --content-inline-start: calc(var(--icon-size) + var(--trigger-gap));

  position: relative;
  max-inline-size: 100%;
  inline-size: 100%;

  &.--status-open {
    --status-color: var(--status-open-color);
  }

  &.--status-closing {
    --status-color: var(--status-closing-color);
  }

  &__trigger {
    appearance: none;
    border: 0;
    background: transparent;
    color: var(--trigger-color);
    display: inline-grid;
    grid-template-columns: var(--icon-size) auto auto;
    align-items: center;
    gap: var(--trigger-gap);
    min-height: var(--trigger-min-height);
    max-inline-size: 100%;
    padding: 0;
    cursor: pointer;
    font: var(--trigger-font);
    text-align: start;
    -webkit-tap-highlight-color: transparent;
  }

  &__trigger--static {
    cursor: default;
  }

  &__trigger:focus-visible {
    outline: var(--sp-ref-spacing-px) solid var(--sp-sys-color-primary);
    outline-offset: var(--sp-ref-spacing-1);
  }

  &__icon {
    --size: var(--icon-size);
    --fill: var(--status-color);
  }

  &__status {
    color: var(--status-color);
    font: var(--status-font);
    text-transform: uppercase;
  }

  &__summary {
    display: inline-flex;
    align-items: center;
    gap: var(--trigger-summary-caret-gap);
    color: var(--summary-color);
  }

  &__caret {
    display: flex;
  }

  &__panel {
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    gap: var(--panel-gap);
    inline-size: min(var(--panel-width), calc(100% - var(--content-inline-start)));
    margin-block-start: var(--panel-margin-block-start);
    margin-inline-start: var(--content-inline-start);
    color: var(--trigger-color);
    font: var(--panel-font);
  }

  &__supporting-details {
    display: flex;
    flex-direction: column;
    gap: var(--panel-gap);
    margin-block-start: var(--panel-margin-block-start);
    margin-inline-start: var(--content-inline-start);
    color: var(--trigger-color);
    font: var(--panel-font);
  }

  &__list,
  &__special-hours,
  &__special-hours-list {
    display: flex;
    flex-direction: column;
    gap: var(--panel-row-gap);
    margin: 0;
  }

  &__special-hours-title {
    margin: 0;
    font: var(--panel-day-font);
  }

  &__row {
    display: grid;
    align-items: baseline;
    grid-template-columns: max-content minmax(0, 1fr);
    gap: var(--sp-ref-spacing-6);
  }

  &__day {
    font: var(--panel-day-font);
  }

  &__times {
    margin: 0;
  }

  &__note {
    margin: 0;
  }
}

.expand-enter-active,
.expand-leave-active {
  transition: all var(--sp-ref-transition-expand-duration) var(--sp-ref-transition-expand-easing);
  overflow: hidden;
  max-height: var(--sp-comp-finder-result-hours-badge-expand-max-height, 40rem);
}

.expand-enter-from,
.expand-leave-to {
  max-height: 0;
  opacity: 0;
  padding-top: 0;
  padding-bottom: 0;
}
</style>
